import { createDateFormatter, useDisplayTimezone, type TimezoneMode } from "@nexus/ui";
import { WidgetState, WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import { CalendarDays } from "lucide-react";
import { AssetImage, firstError, unwrap, Widget } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { Carousel, eisColor } from "@nexus/widget-kit";
import { PROTECTION_OVERVIEW_CAMPAIGN_EVENTS_CONTRACT } from "./types";
import type {
  ProtectionOverviewCampaignEvent,
  ProtectionOverviewCampaignEventsData,
} from "./types";

export interface ProtectionOverviewCampaignEventsConfig {
  title?: string;
}

export const manifest: WidgetManifest = {
  id: "protection-overview-campaign-events",
  name: "Protection Overview - Campaign events",
  description: "One campaign's protected events for one report edition, with drill-down.",
  version: "1.0.0",
  dataRequests: {
    events: {
      source: "protection-overview-campaign-events",
      contract: PROTECTION_OVERVIEW_CAMPAIGN_EVENTS_CONTRACT,
      configurable: true,
    },
  },
};

export const defaultConfig: ProtectionOverviewCampaignEventsConfig = {};

function formatRange(start: string, end: string, timezone: TimezoneMode): string {
  const dateFormat = createDateFormatter(
    timezone,
    { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" },
    "en",
  );
  return `${dateFormat.format(new Date(start))} - ${dateFormat.format(new Date(end))}`;
}

/**
 * A minimal inline gauge: widgets cannot import app/src, so this is not shared.
 * Popularity is a 1–10 score (as on the pre-migration report page's meter).
 */
function PopularityDots({ value }: { value: number }) {
  const filled = Math.round((Math.min(10, Math.max(0, value)) / 10) * 5);
  return (
    <span className="flex items-center gap-0.5" aria-label={`Popularity ${value}/10`}>
      {Array.from({ length: 5 }, (_, index) => (
        <span
          key={index}
          className={
            index < filled
              ? "h-1.5 w-1.5 rounded-full bg-brand-cyan"
              : "h-1.5 w-1.5 rounded-full bg-border"
          }
        />
      ))}
    </span>
  );
}

function EventCard({
  event,
  onOpen,
}: {
  event: ProtectionOverviewCampaignEvent;
  onOpen: (eventId: string) => void;
}) {
  const color = eisColor(event.integrity);
  const timezone = useDisplayTimezone();

  return (
    <button
      type="button"
      disabled={!event.hasReport}
      onClick={() => onOpen(event.id)}
      className="group flex w-72 shrink-0 flex-col gap-3 rounded-lg border border-border bg-surface p-4 text-left transition-colors enabled:hover:border-brand-cyan/40 enabled:hover:bg-surface-elevated disabled:cursor-not-allowed disabled:opacity-60"
    >
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold text-primary">{event.label}</span>
          <span className="truncate text-2xs text-muted">
            {[event.collection ?? event.type, event.channel].filter(Boolean).join(" · ")}
          </span>
        </div>
        <AssetImage
          reference={event.logo}
          alt={event.collectionGroup ?? ""}
          className="h-8 w-8 shrink-0 object-contain"
        />
      </div>
      <div className="flex items-center gap-1.5 text-2xs text-muted">
        <CalendarDays className="h-3.5 w-3.5" />
        {formatRange(event.start, event.end, timezone)}
      </div>
      <div className="flex items-end justify-between border-t border-border pt-3">
        <div className="flex items-start gap-1">
          <span className="text-2xl font-semibold tabular-nums" style={{ color }}>
            {event.integrity}
          </span>
          <span className="mt-auto text-xs text-muted">EIS</span>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-2xs uppercase tracking-wider text-muted">Popularity</span>
          <PopularityDots value={event.popularity} />
        </div>
      </div>
      {!event.hasReport && (
        <span className="text-2xs text-muted" role="status">
          No report yet
        </span>
      )}
    </button>
  );
}

export function Component({
  data,
  config,
  dashboard,
}: WidgetProps<ProtectionOverviewCampaignEventsConfig>) {
  const error = firstError(data, ["events"]);
  const result = unwrap<ProtectionOverviewCampaignEventsData>(data.events);
  const loading = data.events?.isLoading && !result;
  const heading =
    config.title ?? (result ? `Protected events - ${result.campaign.label}` : manifest.name);

  const onOpenEvent = (eventId: string) => {
    dashboard.emitInteraction?.({ type: "event.select", payload: { eventId } });
  };

  return (
    <Widget title={heading}>
      {error ? (
        <WidgetIssueState error={error} />
      ) : loading || !result ? (
        <WidgetSkeleton shape="cards" />
      ) : result.events.length === 0 ? (
        <WidgetState title="No protected events for this campaign in this edition." />
      ) : (
        <div className="p-4">
          <Carousel>
            {result.events.map((event) => (
              <EventCard key={event.id} event={event} onOpen={onOpenEvent} />
            ))}
          </Carousel>
        </div>
      )}
    </Widget>
  );
}

const widgetModule: WidgetModule<ProtectionOverviewCampaignEventsConfig> = {
  manifest,
  Component,
  defaultConfig,
};
export default widgetModule;
